import { CompartmentCard } from "@/src/components/cards";
import { FetchableList } from "@/src/components/fetchableLists";
import { IconTab } from "@/src/components/iconTab";
import { ReferenceTile } from "@/src/components/referenceTile";
import { Input } from "@/src/components/universalInput";
import {
  CardType,
  Consultation,
  FieldAnswer,
  Patient,
  RefAnswer,
  Reference,
  ReferenceCategory,
  referenceCategories,
} from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import apiFetch from "@/src/utils/apiFetch";
import { getItemId, getUserId, uid } from "@/src/utils/utils";
import { useEffect, useState } from "react";
import { BiTestTube } from "react-icons/bi";
import { FaStethoscope } from "react-icons/fa6";
import { IoDocumentTextOutline } from "react-icons/io5";
import { LuCalendarDays } from "react-icons/lu";
import { PiPillBold } from "react-icons/pi";
import { TbReportMedical } from "react-icons/tb";
import { CalendarPage } from "../calendar/calendarPage";
import { DocumentsCompartment } from "../documents/documentsCompartment";
import { ExamDemandsCompartment } from "../hospitalisation/examDemandsCompartment";
import { ExamsCompartment } from "../hospitalisation/examsCompartment";
import { MesuresAndScoresCompartment } from "../hospitalisation/mesuresCompartment";
import { MotiveCompartment } from "../hospitalisation/motiveCompartment";
import { OrdonnancesCompartment } from "../hospitalisation/ordonnancesCompartment";
import { StoryCompartment } from "../hospitalisation/storyCompartment";
import { AntecedentsCompartment } from "../patients/antecedentsHistory";
import { DiagnosticsHistory } from "../patients/diagnosticsHistory";
import ConsultationClinicalExamCard from "./consultationClinicalExamCard";
import { getStandardConfidentiality } from "../patients/confidentialitiesHook";
import { onBeforeClose, openDrawer } from "@/src/components/dialogs";
import { FolderItemTitle } from "@/src/components/folderItemTitle";
import { openIncomeForm } from "../accounting/incomeForm";
import { useRouterHook } from "@/src/utils/routerHook";

function ConsultationPage({
  item,
  patient,
  onChange,
}: {
  item: Consultation;
  patient: Patient;
  onChange?: (item: Consultation) => void;
}) {
  let [tabId, setTabId] = useState("blue");
  const { addParamToUrl } = useRouterHook();

  useEffect(() => {
    addParamToUrl("consultationId", item.id?.split(":")[1]);
    return () => {
      addParamToUrl("consultationId", null);
    };
  }, []);

  const tabs: {
    id: CardType;
    name: string;
    icon: any;
    content: any;
  }[] = [
    {
      id: "blue",

      name: "Informations générales",
      icon: <TbReportMedical size={20} />,
      content: (
        <div className="tabContent c5 gap20">
          <div className="c1 gap15">
            <MotiveCompartment
              current={item?.motives || []}
              patient={patient}
              onChange={(refAnswers) => {
                item.motives = refAnswers;
              }}
            ></MotiveCompartment>

            <DiagnosticsHistory
              current={item.diagnostics || []}
              patient={patient}
              onChange={(refAnswers) => {
                item.diagnostics = refAnswers;
              }}
            ></DiagnosticsHistory>
            <StoryCompartment patient={patient} parentId={getItemId(item)} />
          </div>
          <AntecedentsCompartment patient={patient} />
          <MesuresAndScoresCompartment
            patient={patient}
            parent={item}
            color="simple"
          />

          <div className="c1 gap10">
            <DocumentsCompartment
              patient={patient}
              parent={item}
              color="blue"
            ></DocumentsCompartment>
          </div>
          <div className="c1 gap10">
            <ConsultationClinicalExamCard
              currentConsultation={item}
              patient={patient}
              onChange={(newItem) => {
                onChange?.(newItem);
              }}
            ></ConsultationClinicalExamCard>
          </div>
        </div>
      ),
    },

    {
      id: "orange",
      name: "Examens",
      icon: <BiTestTube size={18} />,
      content: (
        <div className="tabContent grid c2 gap20">
          <ExamsCompartment patient={patient} parent={item}></ExamsCompartment>
          <ExamDemandsCompartment
            patient={patient}
            parent={item}
          ></ExamDemandsCompartment>
        </div>
      ),
    },

    {
      id: "purple",
      name: "Ordonnance",
      icon: <PiPillBold size={18} />,
      content: (
        <div className="tabContent grid c1 gap20">
          <OrdonnancesCompartment
            patient={patient}
            parent={item}
            onChange={(ordonnance) => {
              onChange?.({ ...item });
            }}
          />
        </div>
      ),
    },

    {
      id: "red",
      name: "Rendez-vous",
      icon: <LuCalendarDays size={18} />,
      content: (
        <div className="tabContent grid c1 gap20">
          <CalendarPage userId={getUserId()} patient={patient}></CalendarPage>
        </div>
      ),
    },
  ];

  const selectedTab = tabs.find((tab) => tab.id == tabId);

  return (
    <div className="tabsPage c1" style={{ gap: "20px" }}>
      {
        <div className="grid  gap10">
          {tabs.map((tab) => {
            return (
              <IconTab
                key={uid()}
                color={tab.id as any}
                activeTabId={tabId}
                id={tab.id}
                icon={tab.icon}
                name={tab.name}
                onChange={(tab) => {
                  setTabId(tab);
                }}
              />
            );
          })}
        </div>
      }

      {selectedTab?.content}
    </div>
  );
}

function PatientReferenceCompartment({
  patient,
  current,
  onChange,
  categoryId,
}: {
  patient: Patient;
  current?: (RefAnswer | null | undefined)[];
  onChange?: (newValue: (RefAnswer | null | undefined)[]) => void;
  categoryId: ReferenceCategory;
}) {
  let category = referenceCategories[categoryId];

  const [reload, setReload] = useState(0);
  let [searchKey, setSearchKey] = useState("");
  const [refAnswers, setRefAnswers] = useState(current || []);

  function addRefAnswer(reference: Reference) {
    let newRefAnswers = [...refAnswers];
    newRefAnswers.push({
      patient,
      reference: reference,
      fieldAnswers: [],

      creator: USER?.get(),
    });
    setRefAnswers(newRefAnswers);
    onChange?.(newRefAnswers);
  }

  async function removeRefAnswer(reference: Reference) {
    let newRefAnswers = [...refAnswers];

    let currentRefAnswer = newRefAnswers.find(
      (reference) => reference?.reference?.id == reference?.id
    );
    if (currentRefAnswer?.id)
      await apiFetch("refAnswers", "deleteOne", {
        id: currentRefAnswer.id,
      });

    newRefAnswers = newRefAnswers.filter(
      (refAnswer) => refAnswer?.reference?.id != reference.id
    );
    setRefAnswers(newRefAnswers);
    onChange?.(newRefAnswers);
    setReload((prev) => prev + 1);
  }

  function getMandatoryReferences(
    existingReferences: (Reference | null | undefined)[]
  ): Reference[] {
    let frequentReferences =
      USER?.get()?.speciality?.frequentReferences?.filter(
        (reference) => reference?.category == category.id
      );

    return current
      ?.filter((refAnswer) => {
        return (
          refAnswer?.reference?.category == category.id &&
          !frequentReferences?.find(
            (freq) => freq?.id == refAnswer?.reference?.id
          )
        );
      })
      .filter((refAnswer) => {
        return !existingReferences.find(
          (reference) => reference?.id == refAnswer?.reference?.id
        );
      })
      .map((refAnswer) => {
        return refAnswer?.reference;
      }) as Reference[];
  }

  if (!category) return <div>Categories non définie {categoryId}</div>;

  function getReferenceTiles(references: (Reference | null | undefined)[]) {
    return references?.map((reference) => {
      if (!reference) return;
      let refAnswer = refAnswers.find(
        (item) => item?.reference?.id == reference.id
      );
      return (
        <ReferenceTile
          key={uid()}
          patient={patient}
          reference={reference}
          fieldAnswers={refAnswer?.fieldAnswers}
          isInitiallyActive={refAnswers.some(
            (item) => item?.reference?.id == reference.id
          )}
          onActiveChange={async (isActive) => {
            if (isActive) {
              await addRefAnswer(reference);
            } else {
              await removeRefAnswer(reference);
            }
          }}
          onChange={(field, newValue) => {
            let refAnswer = refAnswers.find(
              (item) => item?.reference?.id == reference.id
            );
            if (!refAnswer) return;

            let currentFieldAnswer: FieldAnswer = refAnswer?.fieldAnswers?.find(
              (fieldAnswer) => fieldAnswer?.field?.id == field.id
            ) || {
              field: field,
              parent: null,
              patient,

              creator: getUserId(),
            };

            currentFieldAnswer.value = newValue;

            refAnswer.fieldAnswers = refAnswer?.fieldAnswers?.filter(
              (fieldAnswer) => fieldAnswer?.field?.id != field.id
            );

            refAnswer?.fieldAnswers?.push(currentFieldAnswer);
          }}
        >
          <Input
            name={"Détails (optionnel)"}
            value={refAnswer?.notes}
            type={"textarea"}
            placeholder={"Texte libre..."}
            rows={5}
            onChange={(newValue) => {
              if (!refAnswer) return;
              refAnswer.notes = newValue;
            }}
          />
        </ReferenceTile>
      );
    });
  }

  return (
    <CompartmentCard
      title={category.name}
      searchKey={searchKey}
      onSearch={(newValue) => {
        setSearchKey(newValue || "");
      }}
      onClick={async () => {
        let reference: any = { category: category.id };
      }}
    >
      <FetchableList
        queryKey={[
          "PatientReferenceCompartment",
          searchKey,
          category.id,
          reload,
        ]}
        queryFn={async () => {
          let frequentReferences =
            USER?.get()?.speciality?.frequentReferences?.filter(
              (reference) => reference?.category == category.id
            ) || [];

          if (frequentReferences.length && !searchKey)
            return frequentReferences;

          return await apiFetch("references", "loadMany", {
            userId: getUserId(),
            categoriesIds: [category.id],
            searchKey,
          });
        }}
      >
        {(data) => (
          <div className="grid c1 gap10">
            {getReferenceTiles(getMandatoryReferences(data || []))}
            {getReferenceTiles(data || [])}
          </div>
        )}
      </FetchableList>
    </CompartmentCard>
  );
}

export async function openConsultationPage({
  consultationId,
  patient,
  onSave,
}: {
  consultationId: string | null;
  patient: Patient;
  onSave: (consultation: Consultation) => void;
}) {
  let consultation = consultationId
    ? await apiFetch("consultations", "loadById", {
        id: getItemId(consultationId),
      })
    : null;

  if (!consultation) {
    consultation = {
      type: "consultation",
      patient,
      creator: getUserId(),
      confidentiality: getStandardConfidentiality(patient),
      functionalSigns: [],
      diagnostics: [],
      motives: [],
      conclusion: "",
      physicalSigns: [],
      readBy: [],
    };

    consultation = await apiFetch("consultations", "addOne", {
      consultation,
    });

    if (!consultation) throw new Error("Consultation not created");
  }

  await openDrawer({
    id: "consultationDrawer",
    title: (
      <FolderItemTitle
        currentPatient={patient}
        parent={consultation}
        showDischargeButton={false}
      ></FolderItemTitle>
    ),

    content: <ConsultationPage patient={patient} item={consultation} />,
    onBeforeClose: onBeforeClose,
    onConfirm: async (e) => {
      let res = await apiFetch("consultations", "addOne", {
        consultation: consultation,
      });

      return await openIncomeForm({ parent: consultation, patient: patient });
    },
  });

  onSave(consultation);
}

export { ConsultationPage, PatientReferenceCompartment };
